import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IsActiveMatchOptions, Router, RouterOutlet } from '@angular/router';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, firstValueFrom, interval, lastValueFrom, map, merge, Observable, of, skip, Subject, Subscription, take, takeUntil, timeout } from 'rxjs';
import { AuthApiService } from './apis/auth-api.service';
import { LocalStorageService } from './lib/local-storage.service';
import { ReservationService } from './lib/reservation.service';
import { ModalDialogComponent } from './modal-dialog/modal-dialog.component';
import { DoubleBookingConflictReservation, DoubleBookingRequest, DoubleBookingResponse, DoubleBookingResponseV2, Reservation, Shift, ShiftType, Table, TablesSection } from './model/reservation';
import { Source } from './model/source';
import * as AppActions from './store/actions/app.actions';
import * as EmployeeActions from './store/actions/employee.actions';
import * as ReservationListActions from './store/actions/reservation-list.actions';
import * as ReservationActions from './store/actions/reservation.actions';
import * as AppSelectors from './store/selectors/app.selectors';
import * as ReservationSelectors from './store/selectors/reservation.selectors';
import * as ReservationListSelectors from './store/selectors/reservation-list.selectors';
import { UtilsService } from './lib/utils.service';
import { environment } from 'src/environments/environment';
import { CapacitorStorageService } from './lib/capacitor-storage.service';
import { AuthService } from './auth/auth.service';
import { UiService } from './lib/ui.service';
import moment from 'moment';
import { Common } from './lib/common-methods';
import { Advertisment } from './model/login-response.dto';
import { Actions } from '@ngrx/effects';
import { ReportApiService } from './apis/report-api.service';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import * as EmployeeSelectors from '@store/selectors/employee.selectors';
import { Location } from '@angular/common';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { Preferences } from '@capacitor/preferences';
import { Employee } from '@model/employee';
import { ExcluceFromOnlineChannelsMode, OnlineReservationCategory, Section, TablesMode, WorkingDate } from '@model/tables-plan';
import { Toast } from 'bootstrap';
import { TablesPlanService } from './tables-plan/tables-plan.service';
declare const bootstrap: any;
import { BroadcastService } from './apis/broadcast-api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BroadcastMessage } from '@model/broadcast-message';
import { ReservationApiService } from './apis/reservation-api.service';
import { ReservationsExportComponent } from './reservation-export/reservations-export.component';

declare let $: any;

const CREDS_SEPERATOR = "_SAKIS_";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'pocket';
  ready = true;
  
  destroy$: Subject<boolean> = new Subject<boolean>();
  initializationFinished$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('outlet') outlet: RouterOutlet | null = null;
  @ViewChild('noConnectionModal') noConnectionModal!: ModalDialogComponent;
  @ViewChild('alertModal') alertModal!: ModalDialogComponent;
  @ViewChild('confirmationModal') confirmationModal!: ModalDialogComponent;
  @ViewChild('doubleBookingConfirmationModal') doubleBookingConfirmationModal!: ModalDialogComponent;
  @ViewChild('deleteJoinedTablesConfirmationModal') deleteJoinedTablesConfirmationModal!: ModalDialogComponent;
  @ViewChild('serviceWarningModal') serviceWarningModal!: ModalDialogComponent;
  @ViewChild('advertismentModal') advertismentModal!: ModalDialogComponent;
  @ViewChild('alreadyReservedTablesModal') alreadyReservedTablesModal!: ModalDialogComponent;
  @ViewChild('manageReservedTablesModal') manageReservedTablesModal!: ModalDialogComponent;
  @ViewChild('orderDetailsModal') orderDetailsModal!: OrderDetailsComponent;
  @ViewChild('helpModal') helpModal!: ModalDialogComponent;
  @ViewChild('manageRegularTablesPlanModal') manageRegularTablesPlanModal!: ModalDialogComponent;
  @ViewChild('settingsModal') settingsModal!: ModalDialogComponent;
  @ViewChild('toast') toastElement!: ElementRef;
  @ViewChild('notAvailableTablesConfirmationOnSubmitModal') notAvailableTablesConfirmationOnSubmitModal!: ModalDialogComponent;
  @ViewChild('reservationsExportModal') reservationsExportModal!: ModalDialogComponent;

  toast: Toast | null = null;

  date$: Observable<Date> = this.store.pipe(select(ReservationListSelectors.selectDate));
  date: Date = new Date();
  isOffline$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectIsOffline));
  isOffline: boolean = false;
  restaurantHasOnlineCategories$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasOnlineCategories));
  alert$: Observable<string | null> = this.store.pipe(select(AppSelectors.selectAlert));
  pendingActionsNumber$: Observable<number> = this.store.pipe(select(ReservationListSelectors.selectPendingActionsNumber));
  tablesPlanPendingActionsNumber$: Observable<number> = this.store.pipe(select(TablesPlanSelectors.selectPendingActionsNumber));
  reservation$: Observable<Reservation | null | undefined> = this.store.pipe(select(ReservationSelectors.selectReservation));
  arrivalTime$: Observable<{ hours: number, mins: number } | null> = this.store.pipe(select(ReservationSelectors.selectArrivalTime));
  readOnlyMode$: Observable<boolean> = this.store.pipe(select(ReservationSelectors.selectReadOnlyMode));
  readOnlyMode: boolean = false;
  isSubmiting$: Observable<boolean> = this.store.pipe(select(ReservationSelectors.selectIsSubmiting));
  isSubmiting: boolean = false;
  stopServiceDate$: Observable<Date | null> = this.store.pipe(select(AppSelectors.selectServiceStopDate));
  stopServiceDate: Date | null = null;
  editMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectEditMode));
  editMode: boolean = false;
  showSurname$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowSurname));
  showSurname: boolean = false;
  showProgress$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowProgress));
  showProgress: boolean = false;
  showTablesConnection$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowTablesConnection));
  showTablesConnection: boolean = false;
  hasOpenedTablesPlan$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectHasOpenedTablesPlan));
  hasOpenedTablesPlan: boolean = true;
  reservationAssignTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectReservationAssignTablesMode));
  reservationAssignTablesMode: boolean = false;
  addJoinedTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAddJoinedTablesMode));
  addJoinedTablesMode: boolean = false;
  assignCategoriesToTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAssignCategoriesToTablesMode));
  assignCategoriesToTablesMode: boolean = false;
  isOnExcludeFromOnlineChannelsMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectIsOnExcludeFromOnlineChannelsMode));
  isOnExcludeFromOnlineChannelsMode: boolean = false;
  tablesMode$: Observable<TablesMode> = this.store.pipe(select(TablesPlanSelectors.selectTablesMode));
  tablesMode: TablesMode = 'Tables';
  assignTablesReservation$: Observable<Reservation | null> = this.store.pipe(select(TablesPlanSelectors.selectAssignTablesReservation));
  assignTablesReservation: Reservation | null = null;
  selectedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSelectedTables));
  selectedTables: Table[] = [];
  allocatedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectAllocatedTables));
  allocatedTables: Table[] = [];
  processedShifts$: Observable<Shift[]> = this.store.pipe(select(ReservationListSelectors.selectProcessedShifts));
  processedShifts: Shift[] = [];
  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedShift: Shift | null | undefined = null;
  editingTable$: Observable<{ table: Table; isNew: boolean; } | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTable));
  editingTable: { table: Table; isNew: boolean; } | null = null;
  conflictingTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectConflictingTables));
  conflictingTables: Table[] = [];
  conflictingReservations$: Observable<Reservation[]> = this.store.pipe(select(TablesPlanSelectors.selectConflictingReservations));
  conflictingReservations: Reservation[] = [];
  reservationsFilteredByShift$: Observable<Reservation[]> = this.store.pipe(select(TablesPlanSelectors.selectReservationsFilteredByShift));
  reservationsFilteredByShift: Reservation[] = [];
  sectionTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSectionTables));
  sectionTables: Table[] = [];
  loggedInemployee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  loggedInemployee: Employee | null = null;
  onlineCategories$: Observable<OnlineReservationCategory[]> = this.store.pipe(select(AppSelectors.selectOnlineCategories));
  onlineCategories: OnlineReservationCategory[] = [];
  isDirty$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectIsDirty));
  isDirty: boolean = false;
  selectedOnlineCategoryToAssign$: Observable<OnlineReservationCategory | null> = this.store.pipe(select(TablesPlanSelectors.selectSelectedOnlineCategoryToAssign));
  selectedOnlineCategoryToAssign: OnlineReservationCategory | null = null;
  excludeFromOnlineChannelsMode$: Observable<ExcluceFromOnlineChannelsMode | null> = this.store.pipe(select(TablesPlanSelectors.selectExcludeFromOnlineChannelsMode));
  excludeFromOnlineChannelsMode: ExcluceFromOnlineChannelsMode | null = null;
  tablesAvailability$: Observable<TablesSection[]> = this.store.pipe(select(ReservationSelectors.selectTablesAvailability));
  tablesAvailability: TablesSection[] = [];

  sectionJoinedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSectionJoinedTables));
  sectionJoinedTables: Table[] = [];

  stopService: boolean = false;

  advertisment$: Observable<Advertisment | null> = this.store.pipe(select(AppSelectors.selectAdvertisment));
  advertisment: Advertisment | null = null;
  advertismentUrl: string | null = null;
  advertismentDesc: string | null = null;
  advertismentCta: string | null = null;
  advertismentTitle: string | null = null;

  reservationsToExport: Reservation[] = [];
  selectedShiftTitleForExport: string = '';
  restaurantTitleForExport: string = '';

  showTags = environment.showTags;
  showUsers = environment.supportApiUrl?.length > 0;

  restaurants: { display: string, value: string }[] = [];
  activeRestaurant = '';
  activeUser = '';

  ver = environment.version;

  falseActiveMatchOptions: IsActiveMatchOptions = {
    fragment: 'ignored',
    matrixParams: 'ignored',
    paths: 'subset',
    queryParams: 'subset'
  };

  exactMatchOptions: IsActiveMatchOptions = {
    fragment: 'exact',
    matrixParams: 'exact',
    paths: 'exact',
    queryParams: 'subset'
  };

  alert: { title: string, template: string } | null = null;
  confirmation: { title: string, template: string, hideConfirm: boolean } | null = null;

  orderDetailsRsvId$: Observable<string | null> = this.store.pipe(select(ReservationListSelectors.selectOrderDetailsReservationId));

  private chainedRequestsStarted: boolean = false;

  conflictedAllocatedTables: Table[] = [];
  conflictedAllocatedTableNames: string = '';

  totalPendingActionsNumber: number = 0;

  validationMessage: string | null = '';

  joinedTablesToDelete: Table[] = [];
  joinedTableNamesToDelete: string = '';

  tableToOnlineCategoryDictionary: { [id: string]: string | null | undefined; } = {};
  tableToExcludeFromOnlineChannelDictionary: { [id: string]: { own: boolean, thirdParty: boolean }; } = {};

  notAvailableTableNames: string = '';

  pendingConfirm: 'doubleBooking' | 'notAssignedToCategory' | null = null;
  private confirmFlags = { doubleBooking: false, notAssignedToCategory: false };

  doubleBookingConflictReservations: DoubleBookingConflictReservation[] = [];

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(e: BeforeUnloadEvent) {

    if (this.isDirty !== true) {
      return;
    }

    // for any modern browser
    e.preventDefault();

    // For IE and Firefox prior to version 4
    if (e) {
      e.returnValue = 'Sure?';
    }

    // For Safari
    return 'Sure?';
  }

  bannerHtml: SafeHtml | null = null;
  public bannerStyles: { [key: string]: string } = {};
  bannerCta: string | null = null;
  bannerUrl: string | null = null;
  private lastBroadcastTimestamp: number | null = null;
  private lastBroadcastKey: string | null = null;

  constructor(
    private translate: TranslateService,
    private store: Store<{ date: Date, sources: Source[] }>,
    private storage: LocalStorageService,
    private capacitorStorage: CapacitorStorageService,
    public utilsService: UtilsService,
    private authService: AuthService,
    private reservationService: ReservationService,
    private reportsService: ReportApiService,
    private uiService: UiService,
    private actionListener$: ActionsSubject,
    private translateService: TranslateService,
    private router: Router,
    private authApiService: AuthApiService,
    private tablesPlanService: TablesPlanService,
    private location: Location,
    private broadcastService: BroadcastService,
    private sanitizer: DomSanitizer,
    private reservationApiService: ReservationApiService

  ) {
    $('body').addClass("platform-" + this.utilsService.getPlatform());
    this.showTags = this.showTags;
  }

  ngOnInit() {

    if (this.utilsService.isTouchDevice()) {
      $('body').addClass('is_touch');
    }

    $('body').on('click', '.btn-header-menu-toggle, .top-header-bg', () => {
      this.closeMenu();
    });

    $('body').on('click', '.btn-header-menu-close', () => {
      this.closeMenu();
    });

    if (this.storage.isAuthenticated() == false) {
      this.ready = true;
    }

    this.isOffline$.pipe(takeUntil(this.destroy$)).subscribe((isOffline) => {
      this.isOffline = isOffline;
    });

    this.alert$.pipe(takeUntil(this.destroy$)).subscribe((alert) => {
      if (this.alertModal == null) {
        this.store.dispatch({ type: AppActions.hideAlert.type });
        return;
      }

      if (alert == null || alert.length <= 0) {
        this.alertModal.closeModal(false);
        return;
      }

      this.alert = { title: '', template: alert };

      this.alertModal.show();
    });

    /*if (this.storage.getUserData() != null) {
      this.ready = true;
    } else {
      this.authService.logIn().pipe(first())
    }*/

    this.authApiService.onLogin(() => {
      this.onLogin();
    });

    this.readOnlyMode$.pipe(takeUntil(this.destroy$)).subscribe((readOnlyMode) => {
      this.readOnlyMode = readOnlyMode;
    });

    this.isSubmiting$.pipe(takeUntil(this.destroy$)).subscribe((isSubmiting) => {
      this.isSubmiting = isSubmiting;
    });

    this.stopServiceDate$.pipe(takeUntil(this.destroy$)).subscribe((dt) => {
      this.stopServiceDate = dt;
      this.checkForServiceWarning();
    });

    this.advertisment$.pipe(takeUntil(this.destroy$)).subscribe((adv) => {
      this.advertisment = adv;
    });

    // disable Http requests queue after some time
    setTimeout(() => {
      this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: false });
    }, 5000);

    this.date$.pipe(takeUntil(this.destroy$)).subscribe((date) => {
      this.date = date;
    });

    this.actionListener$.pipe(takeUntil(this.destroy$)).subscribe((action) => {
      if (action == null) return;

      // Check For Ads
      if (action.type == AppActions.checkForAdvertisment.type) {
        setTimeout(() => {
          this.checkForAdvertisment();
        }, 3000);
      }
      else if (action.type == AppActions.help.type) {
        this.help();
      }
    });

    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((editMode) => {
      this.editMode = editMode;
    });

    this.showSurname$.pipe(takeUntil(this.destroy$)).subscribe((showSurname: boolean) => {
      this.showSurname = showSurname;
    });

    this.showProgress$.pipe(takeUntil(this.destroy$)).subscribe((showProgress: boolean) => {
      this.showProgress = showProgress;
    });

    this.showTablesConnection$.pipe(takeUntil(this.destroy$)).subscribe((showTablesConnection: boolean) => {
      this.showTablesConnection = showTablesConnection;
    });

    this.hasOpenedTablesPlan$.pipe(takeUntil(this.destroy$)).subscribe((hasOpenedTablesPlan: boolean) => {
      this.hasOpenedTablesPlan = hasOpenedTablesPlan;
    });

    this.reservationAssignTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((reservationAssignTablesMode: boolean) => {
      this.reservationAssignTablesMode = reservationAssignTablesMode;
    });

    this.addJoinedTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((addJoinedTablesMode: boolean) => {
      this.addJoinedTablesMode = addJoinedTablesMode;
    });

    this.assignCategoriesToTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((assignCategoriesToTablesMode: boolean) => {
      this.assignCategoriesToTablesMode = assignCategoriesToTablesMode;
    });

    this.isOnExcludeFromOnlineChannelsMode$.pipe(takeUntil(this.destroy$)).subscribe((isOnExcludeFromOnlineChannelsMode: boolean) => {
      this.isOnExcludeFromOnlineChannelsMode = isOnExcludeFromOnlineChannelsMode;
    });

    this.assignTablesReservation$.pipe(takeUntil(this.destroy$)).subscribe((assignTablesReservation: Reservation | null) => {
      this.assignTablesReservation = assignTablesReservation;
    });

    this.selectedTables$.pipe(takeUntil(this.destroy$)).subscribe((selectedTables: Table[]) => {
      this.selectedTables = selectedTables;
    });

    this.allocatedTables$.pipe(takeUntil(this.destroy$)).subscribe((allocatedTables: Table[]) => {
      this.allocatedTables = allocatedTables;
    });

    this.editingTable$.pipe(takeUntil(this.destroy$)).subscribe((editingTable) => {
      this.editingTable = editingTable;
    });

    this.conflictingTables$.pipe(takeUntil(this.destroy$)).subscribe((conflictingTables) => {
      this.conflictingTables = conflictingTables;
    });

    this.conflictingReservations$.pipe(takeUntil(this.destroy$)).subscribe((conflictingReservations) => {
      this.conflictingReservations = conflictingReservations;
    });

    this.reservationsFilteredByShift$.pipe(takeUntil(this.destroy$)).subscribe((reservationsFilteredByShift) => {
      this.reservationsFilteredByShift = reservationsFilteredByShift;
    });

    this.sectionTables$.pipe(takeUntil(this.destroy$)).subscribe((sectionTables) => {
      this.sectionTables = sectionTables;
    });

    this.loggedInemployee$.pipe(takeUntil(this.destroy$)).subscribe((loggedInemployee) => {
      this.loggedInemployee = loggedInemployee;
    });

    this.sectionJoinedTables$.pipe(takeUntil(this.destroy$)).subscribe((sectionJoinedTables) => {
      this.sectionJoinedTables = sectionJoinedTables;
    });

    this.tablesMode$.pipe(takeUntil(this.destroy$)).subscribe((tablesMode) => {
      this.tablesMode = tablesMode;
    });

    this.isDirty$.pipe(takeUntil(this.destroy$)).subscribe((isDirty) => {
      this.isDirty = isDirty;
    });

    this.selectedOnlineCategoryToAssign$.pipe(takeUntil(this.destroy$)).subscribe((selectedOnlineCategoryToAssign) => {
      this.selectedOnlineCategoryToAssign = selectedOnlineCategoryToAssign;
    });

    this.excludeFromOnlineChannelsMode$.pipe(takeUntil(this.destroy$)).subscribe((excludeFromOnlineChannelsMode) => {
      this.excludeFromOnlineChannelsMode = excludeFromOnlineChannelsMode;
    });

    this.tablesAvailability$.pipe(takeUntil(this.destroy$)).subscribe((tablesAvailability) => {
      this.tablesAvailability = tablesAvailability;
    });

    this.onlineCategories$.pipe(takeUntil(this.destroy$)).subscribe((onlineCategories) => {
      this.onlineCategories = onlineCategories
        .filter(category => !category.hidden && category.name !== '—')
        .sort((a, b) => {

          const aOrder = a.displayOrder ?? 0;
          const bOrder = b.displayOrder ?? 0;

          if (aOrder > bOrder) {
            return 1;
          }

          if (aOrder < bOrder) {
            return -1;
          }

          return 0;
        });
    });

    this.processedShifts$.pipe(takeUntil(this.destroy$)).subscribe((processedShifts: Shift[]) => {
      if (processedShifts == null || processedShifts.length <= 0) {
        this.processedShifts = processedShifts;
        return;
      }
      const allDayShift = processedShifts.find(s => s.type === ShiftType.AllDay);

      if (allDayShift == null) {
        return;
      }

      // move All_Day shift last
      this.processedShifts = [...processedShifts.filter(s => s !== allDayShift), allDayShift];
    });

    // tablesPlanPendingActionsNumber$

    merge(this.pendingActionsNumber$, this.tablesPlanPendingActionsNumber$).pipe(takeUntil(this.destroy$)).subscribe(async (actionsNumber: number) => {
      const pendingActionsNumber = await firstValueFrom(this.pendingActionsNumber$);
      const tablesPlanPendingActionsNumber = await firstValueFrom(this.tablesPlanPendingActionsNumber$);
      this.totalPendingActionsNumber = (pendingActionsNumber ?? 0) + (tablesPlanPendingActionsNumber ?? 0);
    });

    setTimeout(() => {
      this.selectedShift$.pipe(takeUntil(this.destroy$)).subscribe((selectedShift: Shift | null | undefined) => {
        this.selectedShift = selectedShift;
      });
    }, 10); // fix ExpressionChangedAfterItHasBeenCheckedError
    this.orderDetailsRsvId$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      if (id == null || id.trim() == '') return;
      this.orderDetailsModal.show(id);
    });

    this.loadBroadcastMessage(); // Run immediately

    setInterval(() => this.loadBroadcastMessage(), environment.broadcastMessageRefreshIntervalMins * 60 * 1000); // Every X mins X * 60 * 1000
  }

  loadBroadcastMessage() {

    this.broadcastService.fetchBroadcastMessages()
      .pipe(take(1), catchError((err) => {
        return of([]);
      }))
      .subscribe((messages) => {
        const venueId = this.activeRestaurant;
        const lang = this.storage.getLanguage();


        // 1) venue with content for current lang
        const candidates = (messages ?? []).filter(m =>
          (m.venueIds?.includes('ALL') || m.venueIds?.includes(venueId)) &&
          !m.excludedIds?.includes(venueId) &&
          !!m.content?.[lang]
        );

        // 2) the latest by timestamp (only those that actually have a timestamp)
        const latest = candidates
          .filter(m => m.timestamp !== undefined)
          .reduce<BroadcastMessage | null>((acc, m) => {
            if (!acc) return m;
            return (m.timestamp! > (acc.timestamp ?? -Infinity)) ? m : acc;
          }, null);

        // 3) If no applicable message → clear banner and reset "last seen"
        if (!latest) {
          this.bannerHtml = null;
          this.bannerStyles = {};
          this.bannerCta = null;
          this.bannerUrl = null;
          this.lastBroadcastTimestamp = null;
          this.lastBroadcastKey = null;
          return;
        }

        // 4) Only update UI if it's the first time, message is newer, OR language changed
        const key = `${venueId}|${lang}|${latest.timestamp ?? 'no-ts'}`;

        if (this.lastBroadcastKey === key) {
          return; // keep current banner unchanged
        }

        this.lastBroadcastKey = key;

        // 5) URL Replacements
        const restaurantName = (() => {
          let name = "";
          this.store.pipe(select(AppSelectors.selectRestaurantName), take(1))
            .subscribe(n => name = n ?? "");
          return name;
        })();
        let url = latest.url?.[lang] ?? null;
        const id = encodeURIComponent(this.storage.getRestaurantId() ?? "");
        const rest = encodeURIComponent(restaurantName ?? "");
        const user = encodeURIComponent(this.activeUser ?? "");
        const campaign = encodeURIComponent(latest.name ?? "");        
        let urlParams = `${url != null && url?.indexOf("?") > - 1 ? '&' : '?'}id=${id}&rest=${rest}&user=${user}&campaign=${campaign}&channel=pocket`;
        
        url = url == null ? null : url += urlParams;                

        // 6) Render
        this.bannerHtml = this.sanitizer.bypassSecurityTrustHtml(latest.content[lang]);
        this.bannerStyles = {
          'background-color': latest.bgColor ?? '#fff3cd',
          'color': latest.fontColor ?? '#000'
        };
        this.bannerCta = latest.cta?.[lang] ?? null;
        this.bannerUrl = url;
      });
  }

  ngAfterViewInit(): void {
    this.toast = bootstrap.Toast.getOrCreateInstance(this.toastElement.nativeElement);
  }

  serviceWarningModalTimer: NodeJS.Timeout | undefined;
  private checkForServiceWarning() {
    if (this.isOnLoginPage() === true) return;

    const currentDate = new Date();

    const warningServiceDate = this.stopServiceDate == null
      ? null
      : moment(this.stopServiceDate).subtract(environment.serviceStopWarningDaysBefore, "days").toDate();

    // Should we show a no pay pop up?
    const showServiceWarningPopUp = warningServiceDate != null && warningServiceDate <= currentDate;

    // Should we stop service as well?
    //this.stopService = this.stopServiceDate != null && this.stopServiceDate <= currentDate;
    this.stopService = false; // never show expired message!

    if (showServiceWarningPopUp === false) return;

    // fail safe
    if (this.serviceWarningModal == null) {
      setTimeout(() => { this.checkForServiceWarning(); }, 100);
      return;
    }

    if (this.serviceWarningModalTimer != null) {
      clearTimeout(this.serviceWarningModalTimer);
    }

    this.serviceWarningModalTimer = setTimeout(() => {
      if (this.isOnLoginPage() === true || this.stopServiceDate == null) return;

      this.serviceWarningModal.show();
    }, 3000);
  }

  advertismentModalTimer: NodeJS.Timeout | undefined;
  private async checkForAdvertisment() {
    try {
      if (this.isOnLoginPage() === true || this.advertisment == null) return;

      // Check if ad has reached the maximum number of display times
      const timesDisplayed = await this.capacitorStorage.getAdsTimesDisplayed();
      if (timesDisplayed >= this.advertisment.maxTimesDisaplayed) return;

      // Check if next display time has passed
      const currentTime = new Date().getTime();
      const displayTime = await this.capacitorStorage.getAdsNextDisplayDayTime();
      if (currentTime < displayTime) return;

      // Update next display time
      const nextDisplayTime = moment(new Date()).add(this.advertisment.hoursInterval, "hours");
      this.capacitorStorage.setAdsNextDisplayDayTime(nextDisplayTime.toDate().getTime());

      // Update number of times displayed
      const updatedDisplayTimes = timesDisplayed + 1;
      this.capacitorStorage.setAdsTimesDisplayed(updatedDisplayTimes);

      this.showAds(10);
    }
    catch (ex) {
      console.log(ex);
    }
  }

  public showAds(timeout: number) {
    if (this.advertisment == null) return;

    const lang = this.storage.getLanguage();
    const desc = this.advertisment?.description;
    let url = this.advertisment?.url;
    let cta = this.advertisment?.cta;
    let title = this.advertisment?.title;

    if (url == null) {
      url = { en: '', el: '', hr: '', pl: '', de: '' };
    }

    if (cta == null) {
      const ctaTxt = this.translateService.instant('messages.advCta');
      cta = { en: ctaTxt, el: ctaTxt, hr: ctaTxt, pl: ctaTxt, de: ctaTxt };
    }

    if (title == null) {
      const titleTxt = this.translateService.instant('messages.advTitle');
      title = { en: titleTxt, el: titleTxt, hr: titleTxt, pl: titleTxt, de: titleTxt };
    }

    this.advertismentDesc = (desc as any)[lang];
    this.advertismentUrl = (url as any)[lang];
    this.advertismentCta = (cta as any)[lang];
    this.advertismentTitle = (title as any)[lang];

    if (this.advertismentDesc == null) {
      this.advertismentDesc = desc?.en || "";
    }

    if (this.advertismentCta == null) {
      this.advertismentCta = cta?.en || "";
    }

    if (this.advertismentUrl != null) {
      this.advertismentUrl = this.advertismentUrl.replace("{{RESTAURANT_ID}}", this.storage.getRestaurantId() ?? "");
    }

    // fail safe
    if (this.advertismentModal == null) {
      setTimeout(() => { this.showAds(timeout); }, 100);
      return;
    }

    if (this.advertismentModalTimer != null) {
      clearTimeout(this.advertismentModalTimer);
    }

    this.advertismentModalTimer = setTimeout(() => {
      if (this.isOnLoginPage() === true || this.advertisment == null) return;

      this.advertismentModal.show();
    }, timeout);
  }

  onAdsClick() {
    this.authApiService.notifyForAdsInterest();

    // Update next display time so that ad is not displayed again (soon)
    const nextDisplayTime = moment(new Date()).add(10, "years");
    this.capacitorStorage.setAdsNextDisplayDayTime(nextDisplayTime.toDate().getTime());

    // Update number of times so that ad is not displayed again (soon)
    const updatedDisplayTimes = 9999;
    this.capacitorStorage.setAdsTimesDisplayed(updatedDisplayTimes);

    this.advertismentModal.hide();
  }

  private async onLogin() {
    this.userDataLoaded();

    // set restaurantId
    const restaurantId = this.storage.getRestaurantId();
    if (restaurantId != null && !isNaN(parseInt(restaurantId))) {
      this.store.dispatch({ type: AppActions.setRestaurantId.type, restaurantId: parseInt(restaurantId) });
    }

    // set date
    this.store.dispatch({ type: ReservationListActions.setDate.type, date: new Date(this.storage.getWorkingDate() ?? '') });

    // set menus
    this.store.dispatch({ type: AppActions.setMenus.type, menus: this.storage.getMenus() });

    // set online categories
    this.store.dispatch({ type: AppActions.setOnlineCategories.type, onlineCategories: this.utilsService.initOnlineCategories(this.storage.getOnlineCategoriesHiddenExcluded()) });

    // set customer types
    this.store.dispatch({ type: AppActions.setCustomerTypes.type, customerTypes: this.storage.getCustomerTypes() });

    // load sources
    // this.store.dispatch({ type: AppActions.loadSources.type });

    // load optionalStatuses
    this.store.dispatch({ type: AppActions.setExtraStatuses.type, extraStatuses: this.storage.getExtraStatuses() });

    // load employee data
    this.store.dispatch({ type: EmployeeActions.loadEmployee.type });

    // load tags
    if (environment.showTags) {
      this.store.dispatch({ type: AppActions.loadTags.type });
    }

    // load list filters and sorting
    const { filters, sortType, sortDesc, selectedShift } = await this.capacitorStorage.getListFiltersAndSorting()
    this.store.dispatch({ type: ReservationListActions.loadListFiltersAndSorting.type, filters, sortType, sortDesc, selectedShift });

    // load pending reservations
    this.store.dispatch({ type: ReservationListActions.setPendingReservations.type, reservations: this.storage.getPendingReservations() });

    // load pending tables plans
    this.store.dispatch({ type: TablesPlanActions.setPendingTablePlans.type, tablePlans: this.storage.getPendingTablePlans() });

    // set communication
    this.store.dispatch({ type: AppActions.setCommunication.type, communication: this.storage.getCommunication() });

    // set service stop date
    this.store.dispatch({ type: AppActions.setServiceStopDate.type, stopDate: this.storage.getServiceStopDate() });

    // set cached reservation lists (for offline usage)
    this.store.dispatch({ type: ReservationListActions.setCachedReservationsList.type, lists: await this.capacitorStorage.getCachedReservationLists() });

    // set reservation list compact view
    this.store.dispatch({ type: ReservationListActions.setCompactView.type, value: this.storage.getCompactListView() });

    // set minimum telephone length
    this.store.dispatch({ type: AppActions.setMinTelephoneLength.type, minTelephoneLength: this.storage.getMinTelephoneLength() });

    // set ads
    this.store.dispatch({ type: AppActions.setAds.type, ads: await this.capacitorStorage.getAds() });

    // load if the user has ever opened the Tables Plan (new feature)
    this.store.dispatch({ type: TablesPlanActions.loadHasOpenedTablesPlan.type });

    // load App Settings
    this.store.dispatch({ type: AppActions.loadAppSettings.type });

    // wait for 1500ms and check if payment warning message must be displayed
    setTimeout(() => {
      this.checkForServiceWarning();
    }, 1500);

    this.loadBroadcastMessage();
  }

  private userDataLoaded() {
    const data = this.storage.getUserData();
    this.activeUser = data?.loggedInemployee?.name ?? '';
    this.restaurants = data?.loggedInemployee?.chainAcounts
      ?.sort((a, b) => a.order - b.order)
      ?.map(r => ({ display: r.restaurantTitle, value: r.restaurantId })) ?? [];

    this.store.dispatch({ type: AppActions.setRestaurants.type, restaurants: this.restaurants });

    this.activeRestaurant = data?.restaurantId ?? '';

    let restaurantName = data?.loggedInemployee?.chainAcounts?.find(restaurant => restaurant.restaurantId === data?.restaurantId)?.restaurantTitle;
    // when the logged in employee has access to only one restaurant
    if (Common.isNullOrEmpty(restaurantName)) {
      restaurantName = data?.restaurantsList?.find(r => r.restaurantId === data?.restaurantId)?.title;
    }

    this.store.dispatch({ type: AppActions.setRestaurantName.type, restaurantName });
  }

  async restaurantChanged(restId: string) {
    await this.authApiService.changeRestaurant(restId);
    //this.sync();
  }

  swipeEnded(ev: any) {
    this.sync(false);
  }

  closeMenu() {
    this.uiService.closeMenu();
  }

  sync(closeMenu = true) {
    const refresh = (this.outlet as any)?.activated?.instance?.refresh;
    if (refresh != null) {
      (this.outlet as any)?.activated?.instance?.refresh();
    }
    if (closeMenu) {
      this.closeMenu();
    }
  }

  isOnLoginPage() {
    return this.router.isActive('/auth/login', true) || this.router.isActive('/auth/auto', false);
  }


  isWebPlatform(): boolean {
    return this.utilsService.getPlatform() === 'web';
  }

  isOnReservationsPage() {
    return this.router.isActive('/reservations', this.exactMatchOptions) ||
      this.router.isActive('/reservations/search', this.exactMatchOptions) ||
      this.router.isActive('/', this.exactMatchOptions);
  }

  isOnBlockingPage() {
    return this.router.isActive('/blocking', this.falseActiveMatchOptions);
  }

  isOnReservationPage() {
    return this.router.isActive('/reservations/add', this.falseActiveMatchOptions)
      || this.router.isActive('/reservations/copy', this.falseActiveMatchOptions)
      || this.router.isActive('/reservations/edit', this.falseActiveMatchOptions);
  }

  isOnGlobalSearchPage() {
    return this.router.isActive('/reservations/search', this.falseActiveMatchOptions);
  }

  isOnWalkinPage() {
    return this.router.isActive('/walkin/add', this.falseActiveMatchOptions);
  }

  isOnReportsPage() {
    return this.router.isActive('/reports', this.exactMatchOptions);
  }

  isOnTablesPlanPage() {
    return this.router.isActive('/tables-plan', this.exactMatchOptions);
  }

  isOnSettingsPage() {
    return this.router.isActive('/settings', this.exactMatchOptions);
  }

  disableMenuItems() {
    return this.isOnTablesPlanPage()
      && (this.editMode || this.reservationAssignTablesMode || this.selectedTables.length > 0 || this.addJoinedTablesMode);
  }

  disableCreateReservationButtons() {
    return this.isOnTablesPlanPage() && this.reservationAssignTablesMode;
  }

  dateChange(date: Date) {
    this.store.dispatch({ type: ReservationListActions.setDate.type, date });
  }

  logout(event: MouseEvent) {
    if (event != null) {
      // user explicitly logged out
      this.capacitorStorage.setExplicitLogout(true);
      this.capacitorStorage.clearValue('TablesPlanOptions');
      this.capacitorStorage.clearValue('LatestActiveView');
    }
    this.authService.logout();
    this.loadBroadcastMessage();
  }

  async submitReservationWithOfflineAlert() {
    if (this.readOnlyMode) {
      return;
    }

    if (this.isOffline) {
      this.noConnectionModal.show();
      this.location.back();
      return;
    }

    this.submitReservation(null);
  }

  async goToDashboard() {
    this.reportsService.getDashboardUrl(this.storage.getRestaurantId()).subscribe(url => {
      console.log(url);
      window.open(url, "_blank");
    }, error => {
      this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translateService.instant('messages.somethingWrongHappened') });
      console.log(error);
    });
  }

  async alreadyReservedTablesExist() {
    const reservation = await firstValueFrom(this.reservation$);

    const allocatedTables =
      reservation?.reservationDetails?.allocatedTables
        ? reservation.reservationDetails.allocatedTables
          .split(',')
          .filter(t => t != null && t.trim().length > 0)
          .map(t => t.trim()) : [];

    const tables = this.getTablesByTableNames(allocatedTables);
    const notAvailableTables: Table[] = tables.filter(t => !t.available);

    if (notAvailableTables.length > 0) {
      this.notAvailableTableNames = notAvailableTables.map(t => t.tableName).join(', ');
      this.notAvailableTablesConfirmationOnSubmitModal.show();
      return;
    }

    this.submitReservationWithOfflineAlert();
  }

  getTablesByTableNames(tableNames: string[]) {
    const tables: Table[] = [];

    for (let section of this.tablesAvailability) {
      for (let table of section.sectionTables) {
        const tableName = table.tableName?.trim();
        if (tableName == null || !tableNames.includes(tableName)) {
          continue;
        }

        tables.push(table);
      }
    }

    return tables;
  }

  onCloseNotAvailableTablesConfirmationOnSubmit(result: boolean) {
    if (result !== true) {
      return;
    }

    this.submitReservationWithOfflineAlert();
  }

  formatDate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // months are 0-based
    const day = pad(date.getDate());

    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  private buildDoubleBookingRequest(reservation: Reservation): DoubleBookingRequest | null {
    const restaurantId = this.storage.getRestaurantId();

    if (!reservation?.reservationDetails || !reservation?.customerDetails || !restaurantId) {
      return null;
    }

  const email = reservation.customerDetails.mail?.trim();
  const phone = reservation.customerDetails.telephone?.trim();
  const bookingId = reservation.reservationDetails.roomBookingId?.trim();
  const roomNumber = reservation.reservationDetails.roomNumber?.trim();
  const name = reservation.customerDetails.name?.trim();
  const surname = reservation.customerDetails.surname?.trim();

  // skip backend call entirely if nothing useful to check
  if (!email && !phone && !bookingId && !roomNumber && !name && !surname) {
    return null;
  }

  const arrivalTime = this.formatDate(new Date(reservation.reservationDetails.arrivalTime));

  return {
    restaurantId,
    surname: reservation.customerDetails.surname ?? '',
    name: reservation.customerDetails.name ?? '',
    bookingId: reservation.reservationDetails.roomBookingId ?? '',
    roomNumber: reservation.reservationDetails.roomNumber ?? '',
    mail: reservation.customerDetails.mail ?? '',
    externalCustomerId: undefined,
    phone: reservation.customerDetails.telephone ?? '',
    arrival: arrivalTime
  };
}

  private isDoubleBookingResponseV2(
    result: DoubleBookingResponse | DoubleBookingResponseV2
  ): result is DoubleBookingResponseV2 {
    return Array.isArray((result as DoubleBookingResponseV2).conflictReservations);
  }

  getDoubleBookingCategoryName(reservation: DoubleBookingConflictReservation): string {
    const lang = this.storage.getLanguage();

    if (lang === 'el') {
      return reservation.categoryNameLocal || reservation.categoryNameEnglish || '-';
    }

    return reservation.categoryNameEnglish || reservation.categoryNameLocal || '-';
  }


  checkDoubleBooking(reservation: Reservation): Observable<DoubleBookingResponse> {
    const request = this.buildDoubleBookingRequest(reservation);

    if (request == null) {
      return of({ conflict: false, matchedFields: [], success: true });
    }

    return this.reservationApiService.doubleBookingCheck(request).pipe(
      catchError(() => {
        // Treat failure as NO CONFLICT per requirements
        return of({ conflict: false, matchedFields: [], success: false });
      })
    );
  }

  checkDoubleBookingExtended(reservation: Reservation): Observable<DoubleBookingResponseV2> {
    const request = this.buildDoubleBookingRequest(reservation);

    if (request == null) {
      return of({
        conflict: false,
        matchedFields: [],
        success: true,
        conflictReservations: []
      });
    }

    return this.reservationApiService.doubleBookingCheckV2(request).pipe(
      catchError(() => {
        // Treat failure as NO CONFLICT per requirements
        return of({
          conflict: false,
          matchedFields: [],
          success: false,
          conflictReservations: []
        });
      })
    );
  }


  async submitReservation(value: any) {
    if (this.readOnlyMode || this.isSubmiting) return;

    // Reset flags for the new reservation flow
    this.confirmFlags = { doubleBooking: false, notAssignedToCategory: false };
    this.pendingConfirm = null;
    this.doubleBookingConflictReservations = [];

    await this.trySubmitFlow();
  }


  private async trySubmitFlow() {
    const reservation = await firstValueFrom(this.reservation$);
    const arrivalTime = await firstValueFrom(this.arrivalTime$);
    if (!reservation) return;

    // 1) standard validation
    this.alert = reservation.reservationDetails.walkIn === true && reservation.reservationDetails.reservationId == null
      ? this.reservationService.walkinIsValid(reservation)
      : this.reservationService.reservationIsValid(reservation, arrivalTime);

    if (this.alert) {
      this.alertModal.show();
      return;
    }

    // 2) “not assigned to category” confirmation (existing)
    const hasOnlineReservations = this.storage.getHasOnlineReservations();
    const restaurantHasOnlineCategories = await firstValueFrom(this.restaurantHasOnlineCategories$);
    const hasNotCategory = reservation.reservationDetails.onlineCategory == null
      || reservation.reservationDetails.onlineCategory.id == null;
    const isWalkin = reservation.reservationDetails.walkIn;

    if (hasOnlineReservations && hasNotCategory && restaurantHasOnlineCategories && !isWalkin && !this.confirmFlags.notAssignedToCategory) {
      this.confirmation = {
        title: this.translate.instant('common.warning'),
        template: this.translate.instant('reservations.reservationNotAssignedToCategory'),
        hideConfirm: false
      };
      this.pendingConfirm = 'notAssignedToCategory';
      setTimeout(() => {
        this.confirmationModal.show();
      }, 100);

      return;
    }

    // Double-booking confirmation (NEW reservations ONLY)
    const isEditMode = !!reservation.reservationDetails?.reservationId;

    if (!isEditMode &&
      this.utilsService.canWrite('enableDoubleBooking') &&
      !this.confirmFlags.doubleBooking) {

      let doubleBookingResult: DoubleBookingResponse | DoubleBookingResponseV2;

      try {
        const doubleBookingCheck$ = this.utilsService.canWrite('doubleBookingExtraInfo')
          ? this.checkDoubleBookingExtended(reservation)
          : this.checkDoubleBooking(reservation);

        doubleBookingResult = await firstValueFrom(
          doubleBookingCheck$.pipe(
            timeout(environment.doubleBookingTimeoutMs ?? 3000)
          )
        );
      } catch (err) {
        // timeout or network failure → continue without popup
        doubleBookingResult = { conflict: false, matchedFields: [], success: false };
      }

      //  ensure we continue flow in case of network failure
      if (!doubleBookingResult.success) {
        this.confirmFlags.doubleBooking = true;
      }

      // show popup ONLY when backend returned a conflict
      if (doubleBookingResult.success && doubleBookingResult.conflict) {

        const hasExtraInfo = this.utilsService.canWrite('doubleBookingExtraInfo');

        this.doubleBookingConflictReservations =
          hasExtraInfo && this.isDoubleBookingResponseV2(doubleBookingResult)
            ? doubleBookingResult.conflictReservations ?? []
            : [];

        this.confirmation = {
          title: this.translate.instant('reservations.doubleBooking.title'),
           template: '',
          hideConfirm: !this.utilsService.canWrite('skipDoubleBooking')
        };

        this.pendingConfirm = 'doubleBooking';

        setTimeout(() => {
          this.doubleBookingConfirmationModal?.show();
        }, 200);

        return;
      }
    }

    

    // 4) all clear — finally submit
    this.store.dispatch({ type: ReservationActions.submitReservation.type });
  }

  onCloseConfirmation(value: any) {
    if (this.readOnlyMode) return;

    if (value === true) {
      if (this.pendingConfirm === 'doubleBooking') this.confirmFlags.doubleBooking = true;
      if (this.pendingConfirm === 'notAssignedToCategory') this.confirmFlags.notAssignedToCategory = true;

      this.pendingConfirm = null;
      this.trySubmitFlow();
    } else {
      // user cancelled → reset everything
      this.pendingConfirm = null;
      this.confirmFlags = { doubleBooking: false, notAssignedToCategory: false };
    }
  }

  onCloseAlert(value: any) {
    this.store.dispatch({ type: AppActions.hideAlert.type });
  }

  onCloseDeleteJoinedTablesConfirmation(value: any) {
    if (value == false) {
      return;
    }

    if (this.assignCategoriesToTablesMode) {
      this.store.dispatch({
        type: TablesPlanActions.assignOnlineCategoryToTables.type,
        tables: this.allocatedTables,
        alteredTables: this.getOnlineCategoryAlteredTables(),
        modifiedBy: this.loggedInemployee?.surname
      });
    } else if (this.isOnExcludeFromOnlineChannelsMode) {
      this.store.dispatch({
        type: TablesPlanActions.excludeFromOnlineChannels.type,
        tables: this.allocatedTables,
        alteredTables: this.getExcludeFromOnlineChannelsAlteredTables(),
        modifiedBy: this.loggedInemployee?.surname
      });
    }
  }

  submitSettings() {
    this.store.dispatch({ type: AppActions.saveEditingAppSettings.type });
  }

  today() {
    this.store.dispatch({ type: ReservationListActions.setDate.type, date: new Date() });
  }

  help() {
    this.helpModal.show();
  }

  showSettingsModal() {
    this.store.dispatch({ type: TablesPlanActions.loadSettings.type });
    this.settingsModal.show();
  }

  hideSettingsModal() {
    this.settingsModal.hide();
  }

async goToAdminPanel() {
    const lang = this.storage.getLanguage() == "el" ? "el" : "eng";

    const restaurantId = this.storage.getRestaurantId();
    const employeeId = this.loggedInemployee?.employeeId;
    const token = this.storage.getServerToken();
    const deviceId = this.storage.getDeviceId();
    const workingDate = this.storage.getWorkingDate();


    if (!restaurantId || !employeeId || !token || !deviceId) {
      this.store.dispatch({
        type: AppActions.showAlert.type,
        alert: this.translateService.instant('messages.somethingWrongHappened')
      });
      return;
    }

    const params = new URLSearchParams();

    params.set('logInWithEmployee', 'true')
    params.set('employeeId', employeeId);
    params.set('restaurantId', restaurantId);
    params.set('lang', lang);

    // if (workingDate != null && workingDate.trim() !== '') {
    //   params.set('workingDate', workingDate);
    // }

    const url = `${environment.webServerUrl}/adminPanel?${params.toString()}`;

    window.open(url, '_blank');
  }

  async goToMarketingTool() {
  const restaurantId = this.storage.getRestaurantId();
  const employeeId = this.loggedInemployee?.employeeId;
  const deviceId = this.storage.getDeviceId();

  if (!restaurantId || !employeeId) {
    this.store.dispatch({
      type: AppActions.showAlert.type,
      alert: this.translateService.instant('messages.somethingWrongHappened')
    });
    return;
  }

  const params = new URLSearchParams();

  params.set('logInWithEmployee', 'true');
  params.set('employeeId', employeeId);
  params.set('restaurantId', restaurantId);
  params.set('deviceId', deviceId);

  const url = `${environment.marketingToolUrl}/login?${params.toString()}`;


  window.open(url, '_blank');
}

  isTodaySelected() {
    const moment1 = moment(this.date);
    const moment2 = moment(new Date());
    return moment1.isSame(moment2, 'date');
  }

  setTablesPlanMode(editMode: boolean) {
    const restaurantId = this.storage.getRestaurantId();
    this.store.dispatch({ type: TablesPlanActions.setMode.type, editMode, restaurantId, modifiedBy: this.loggedInemployee?.surname, workingDate: this.date });
  }

  showManageRegularTablesPlanModal() {
    this.manageRegularTablesPlanModal.show();
  }

  saveTablesPlan() {
    this.store.dispatch({ type: TablesPlanActions.saveTablesPlan.type });
  }

  restoreRegularTablesPlan() {
    this.manageRegularTablesPlanModal.hide();
    this.store.dispatch({ type: TablesPlanActions.restoreRegularTablesPlan.type, modifiedBy: this.loggedInemployee?.surname });
  }

  cancelManageRegularTablesPlan() {
    this.manageRegularTablesPlanModal.hide();
  }

  saveTablesPlanAsRegular() {
    this.manageRegularTablesPlanModal.hide();
    this.store.dispatch({ type: TablesPlanActions.saveTablesPlanAsRegular.type, workingDate: this.date, modifiedBy: this.loggedInemployee?.surname });
  }

  setShowSurname(show: boolean) {
    this.store.dispatch({ type: TablesPlanActions.setShowSurname.type, show });
  }

  setShowProgress(show: boolean) {
    this.store.dispatch({ type: TablesPlanActions.setShowProgress.type, show });
  }

  setShowTablesConnection(show: boolean) {
    this.store.dispatch({ type: TablesPlanActions.setShowTablesConnection.type, show });
  }

  getOnlineCategoryAlteredTables() {
    const alteredTables: Table[] = [];

    for (let table of this.sectionTables) {
      const previousOnlineCategoryId = this.tableToOnlineCategoryDictionary[table.tableId];

      const isSelected = this.allocatedTables.some(t => t.tableId === table.tableId);

      const newOnlineCategoryId = isSelected
        ? this.selectedOnlineCategoryToAssign?.id
        : (previousOnlineCategoryId === this.selectedOnlineCategoryToAssign?.id ? null : previousOnlineCategoryId);

      if (previousOnlineCategoryId != newOnlineCategoryId) {
        alteredTables.push(table);
      }
    }
    return alteredTables;
  }

  getExcludeFromOnlineChannelsAlteredTables() {
    const alteredTables: Table[] = [];

    for (let table of this.sectionTables) {
      const previousExcludeOnlineChannelEntry = this.tableToExcludeFromOnlineChannelDictionary[table.tableId];
      const previousValue = this.excludeFromOnlineChannelsMode === 'Own'
        ? previousExcludeOnlineChannelEntry.own
        : previousExcludeOnlineChannelEntry.thirdParty;

      const newValue = this.allocatedTables.some(t => t.tableId === table.tableId);

      if (previousValue != newValue) {
        alteredTables.push(table);
      }
    }
    return alteredTables;
  }

  closeAssignTablesMode(applyChanges: boolean) {

    if (this.editMode) {

      if (this.addJoinedTablesMode) {
        if (applyChanges && this.allocatedTables.length > 0) {

          // check if joined table already exists
          const allocatedTableIds = this.allocatedTables.map(t => t.tableId).sort().join(';');

          if (this.allocatedTables.length === 1 || this.sectionJoinedTables.some(joinedTable => {
            const tableIds = (joinedTable.joinTablesIds ?? '')?.split(';').sort().join(';');
            return tableIds === allocatedTableIds;
          })) {
            this.store.dispatch({
              type: TablesPlanActions.setAddJoinedTablesMode.type,
              addJoinedTablesMode: false
            });

            this.validationMessage = this.translate.instant('tablesPlan.editMode.joinTableExistsMessage', { name: this.allocatedTables.map(t => t.tableName).join(', ') });
            this.toast?.show();

            return;
          }

          this.store.dispatch({
            type: TablesPlanActions.addJoinedTable.type,
            allocatedTableIds: this.allocatedTables.map(t => t.tableId),
            modifiedBy: this.loggedInemployee?.surname
          });
        } else {
          this.store.dispatch({
            type: TablesPlanActions.setAddJoinedTablesMode.type,
            addJoinedTablesMode: false
          });
        }
      } else if (this.assignCategoriesToTablesMode) {
        if (applyChanges) {

          const alteredTables: Table[] = this.getOnlineCategoryAlteredTables();

          // check if joined tables affected
          this.joinedTablesToDelete = this.tablesPlanService.getJoinedParticipantTables(this.sectionJoinedTables, alteredTables);
          if (this.joinedTablesToDelete.length > 0) {
            this.joinedTableNamesToDelete = this.joinedTablesToDelete.map(t => t.tableName).join(', ')
            this.deleteJoinedTablesConfirmationModal.show();
            return;
          } else {
            this.joinedTableNamesToDelete = '';
          }

          this.store.dispatch({
            type: TablesPlanActions.assignOnlineCategoryToTables.type,
            tables: this.allocatedTables,
            alteredTables,
            modifiedBy: this.loggedInemployee?.surname
          });
        } else {
          this.store.dispatch({
            type: TablesPlanActions.setSelectedOnlineCategoryToAssign.type,
            onlineCategory: null
          });
        }
      } else if (this.isOnExcludeFromOnlineChannelsMode) {
        if (applyChanges) {

          const alteredTables: Table[] = this.getExcludeFromOnlineChannelsAlteredTables();

          // check if joined tables affected
          this.joinedTablesToDelete = this.tablesPlanService.getJoinedParticipantTables(this.sectionJoinedTables, alteredTables);
          if (this.joinedTablesToDelete.length > 0) {
            this.joinedTableNamesToDelete = this.joinedTablesToDelete.map(t => t.tableName).join(', ')
            this.deleteJoinedTablesConfirmationModal.show();
            return;
          } else {
            this.joinedTableNamesToDelete = '';
          }

          this.store.dispatch({
            type: TablesPlanActions.excludeFromOnlineChannels.type,
            tables: this.allocatedTables,
            alteredTables,
            modifiedBy: this.loggedInemployee?.surname
          });
        } else {
          this.store.dispatch({
            type: TablesPlanActions.setExcludeFromChannelsMode.type,
            mode: ''
          });
        }
      } else {
        // if (applyChanges) {
        //   this.store.dispatch({
        //     type: TablesPlanActions.saveEditingTable.type,
        //   });
        // } else {
        //   this.store.dispatch({
        //     type: TablesPlanActions.cancelEditTable.type,
        //   });
        // }

        if (applyChanges) {
          this.saveTablesPlan();
        } else {
          this.setTablesPlanMode(false);
        }
      }

    } else {
      // assign tables
      if (applyChanges) {

        // const conflictingTableNames = this.conflictingTables.map(t => t.tableName);
        const allocatedTableNames = this.allocatedTables.map(t => t.tableName);

        this.conflictedAllocatedTables = this.conflictingTables
          .filter(t => allocatedTableNames.includes(t.tableName))
          .sort(Common.tableOrderComperator);
        this.conflictedAllocatedTableNames = this.conflictedAllocatedTables.map(t => t.tableName).join(', ');

        if (this.conflictedAllocatedTables.length > 0) {
          this.alreadyReservedTablesModal.show();
          return;
        }

        this.assignTables(this.allocatedTables);
      }

      this.resetAssignTablesMode();
    }
  }

  private readonly initialCategoryNotePrefix = '(Init cat.:';

  private upsertInitialCategoryNote(
    notes: string | null | undefined,
    categoryLabel: string
  ): string {
    const lines = (notes ?? '')
      .split(/\r?\n/)
      .map(x => x.trim())
      .filter(x => x.length > 0);

    const newLine = `${this.initialCategoryNotePrefix} ${categoryLabel})`;
    const existingIndex = lines.findIndex(x => x.startsWith(this.initialCategoryNotePrefix));

    if (existingIndex >= 0) {
      lines[existingIndex] = newLine;
    } else {
      lines.push(newLine);
    }

    return lines.join('\n');
  }

  private hasAllocatedTables(value: string | null | undefined): boolean {
    return (value ?? '')
      .split(',')
      .map(x => x.trim())
      .filter(x => x.length > 0)
      .length > 0;
  }

  private getOnlineCategoryLabel(category: OnlineReservationCategory | null | undefined): string | null {
    if (!category?.id) {
      return null;
    }

    return (
      category.iconCaption?.trim() ||
      category.nameLocalized?.trim() ||
      category.name?.trim() ||
      category.id
    );
  }

  assignTables(allocatedTables: Table[]) {
    const existingReservation = this.assignTablesReservation;
    const existingReservationOnlineCategory = existingReservation?.reservationDetails?.onlineCategory;

    const currentNotes = existingReservation?.reservationDetails?.notes ?? '';
    const hadAllocatedTablesBefore = this.hasAllocatedTables(existingReservation?.reservationDetails?.allocatedTables);
    const isAssigningTablesNow = (allocatedTables?.length ?? 0) > 0;

    this.store.dispatch({
      type: ReservationActions.setReservationAllocatedTables.type,
      allocatedTables: allocatedTables.map(t => t.tableName)
    });
    this.store.dispatch({
      type: ReservationListActions.setReservationAllocatedTables.type,
      reservation: existingReservation,
      tables: allocatedTables
    });

    const onlineCategory = this.utilsService.autoSetOnlineCategory(allocatedTables);

    if (onlineCategory != undefined) {
      this.store.dispatch({
        type: ReservationActions.setOnlineCategory.type,
        onlineCategory
      });
    }

    if (
      !hadAllocatedTablesBefore &&
      isAssigningTablesNow &&
      existingReservationOnlineCategory?.id
    ) {
      const categoryLabel = this.getOnlineCategoryLabel(existingReservationOnlineCategory);

      if (categoryLabel) {
        const updatedNotes = this.upsertInitialCategoryNote(currentNotes, categoryLabel);
        console.log('updatedNotes', updatedNotes);

        if (updatedNotes !== currentNotes) {
          this.store.dispatch({
            type: ReservationActions.setReservationNotes.type,
            notes: updatedNotes
          });
        }

      }
    }

    this.resetAssignTablesMode();
  }

  resetAssignTablesMode() {
    this.store.dispatch({ type: TablesPlanActions.setReservationAssignTablesMode.type, assignMode: false, reservation: null });
    this.store.dispatch({ type: TablesPlanActions.setSelectedTables.type, tables: [] });
    this.store.dispatch({ type: TablesPlanActions.setAllocatedTables.type, tables: [] });
    this.store.dispatch({ type: ReservationListActions.setEditingReservationId.type, reservationId: null });
  }

  setOnlineCategoryToAssign(onlineCategory: OnlineReservationCategory) {
    this.store.dispatch({
      type: TablesPlanActions.setSelectedOnlineCategoryToAssign.type,
      onlineCategory
    });

    this.tableToOnlineCategoryDictionary = {};
    for (let table of this.sectionTables) {
      this.tableToOnlineCategoryDictionary[table.tableId] = table.onlineCategory?.id;
    }
  }

  clickOnlineCategoriesButton() {
    if ((this.onlineCategories ?? []).length > 0) {
      return;
    }

    this.validationMessage = this.translate.instant('tablesPlan.editMode.noCategoriesForAssigment');
    this.toast?.show();
  }

  excludeFromOnlineOwnChannels() {
    this.store.dispatch({
      type: TablesPlanActions.setExcludeFromChannelsMode.type,
      mode: 'Own'
    });
    this.updatetableToExcludeFromOnlineChannelDictionary();
  }

  excludeFromOnlineThirdPartyChannels() {
    this.store.dispatch({
      type: TablesPlanActions.setExcludeFromChannelsMode.type,
      mode: 'ThirdParty'
    });
    this.updatetableToExcludeFromOnlineChannelDictionary();
  }

  updatetableToExcludeFromOnlineChannelDictionary() {
    this.tableToExcludeFromOnlineChannelDictionary = {};
    for (let table of this.sectionTables) {
      this.tableToExcludeFromOnlineChannelDictionary[table.tableId] = { own: table.excludeOnline === true, thirdParty: table.excludeOnlineThirdParty === true };
    }
  }

  getShiftTitle(shiftType: ShiftType | undefined) {
    return this.reservationService.translateShiftTitle(shiftType);
  }

  selectTablesPlanShift(shift: Shift) {
    this.store.dispatch({ type: ReservationListActions.setSelectedShift.type, shift });
  }

  goBack() {
    this.location.back();
  }

  // already reserved tables modal
  manageTables() {
    this.alreadyReservedTablesModal.hide();
    this.manageReservedTablesModal.show();

    // find conflicting reservations
    const conflictingReservations = this.reservationService.getConflictingReservationsForTableAllocation(
      this.assignTablesReservation, this.reservationsFilteredByShift, this.allocatedTables
    );

    this.store.dispatch({
      type: TablesPlanActions.setConflictingReservations.type,
      reservations: conflictingReservations
    });
  }

  cancelAssignTables() {
    this.alreadyReservedTablesModal.hide();
    this.resetAssignTablesMode();
  }

  confirmAssignReservedTables() {
    this.alreadyReservedTablesModal.hide();
    this.assignTables(this.allocatedTables);
  }

  // manage reserved tables modal
  swapTables() {
    this.manageReservedTablesModal.hide();

    if (this.conflictingReservations.length > 1) {
      return;
    }

    const conflictingReservation = this.conflictingReservations[0];

    // keep a copy of the editing's reservation tables
    const editingReservationAllocatedTables = this.assignTablesReservation?.reservationDetails.allocatedTables ?? '';

    // // assign to the editing's reservation the tables of the conflicting one and submit
    // this.store.dispatch({
    //   type: ReservationActions.setReservationAllocatedTables.type,
    //   allocatedTables: conflictingReservation.reservationDetails.allocatedTables
    // });

    // // set editing reservation the conflicting one
    // this.store.dispatch({
    //   type: ReservationActions.setReservation.type,
    //   reservation: conflictingReservation
    // });

    // // assign to the editing's reservation the tables of the conflicting one and submit
    // this.store.dispatch({
    //   type: ReservationActions.setReservationAllocatedTables.type,
    //   allocatedTables: editingReservationAllocatedTables
    // });

    // this.store.dispatch({
    //   type: TablesPlanActions.setSwapTableNames.type,
    //   tableNames: (editingReservationAllocatedTables ?? '').split(',')
    // });

    const allocatedTableNames = conflictingReservation.reservationDetails.allocatedTables
      .split(',')
      .filter(n => !Common.isNullOrEmpty(n));

    const allocatedTables = this.sectionTables.filter(t => allocatedTableNames.includes(t.tableName));

    this.assignTables(allocatedTables);

    this.store.dispatch({
      type: TablesPlanActions.setReservationAllocatedTablesAndSubmit.type,
      reservation: conflictingReservation,
      allocatedTables: editingReservationAllocatedTables.split(',').filter(n => !Common.isNullOrEmpty(n))
    });

    this.resetAssignTablesMode();
  }

  cancelManageTables() {
    this.manageReservedTablesModal.hide();
    this.resetAssignTablesMode();
  }

  getReservedTables() {
    this.manageReservedTablesModal.hide();
    this.assignTables(this.allocatedTables);

    if (this.conflictingReservations.length <= 0) {
      return;
    }

    for (let conflictingReservation of this.conflictingReservations) {
      this.store.dispatch({
        type: TablesPlanActions.setReservationAllocatedTablesAndSubmit.type,
        reservation: conflictingReservation,
        allocatedTables: []
      });
    }
  }

  addWalkIn() {
    if (!this.isTodaySelected()) {
      this.store.dispatch({
        type: AppActions.showAlert.type,
        alert: this.translate.instant('messages.invalidWalkinDate', { date: moment(this.date).format('DD-MM-YYYY') })
      });
      return;
    }

    this.router.navigate(['/walkin/add']);
  }

  downloadApk() {
    var link = document.createElement("a");
    // If you don't know the name or want to use
    // the webserver default set name = ''
    link.setAttribute('download', 'pocket_tables_plan.apk');
    link.href = '/assets/app-debug.apk';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  showReservationsExportModal() {
    const activeComponent = (this.outlet as any)?.activated?.instance as {
      reservations?: Reservation[];
    };

    const reservations = [...(activeComponent?.reservations ?? [])];

    this.reservationsToExport = this.filterReservationsForSelectedShift(reservations);


    this.selectedShiftTitleForExport = this.selectedShift != null
      ? this.getShiftTitle(this.selectedShift.type) ?? 'All Day'
      : 'All Day';

    this.restaurantTitleForExport =
      this.restaurants.find(r => r.value === this.activeRestaurant)?.display ?? '';

    this.reservationsExportModal.show();
  }

  hideReservationsExportModal() {
    this.reservationsExportModal.hide();
  }

  private filterReservationsForSelectedShift(reservations: Reservation[]): Reservation[] {
    const shift = this.selectedShift;

    if (!shift || shift.isFullDay === true) {
      return [...reservations];
    }

    return reservations.filter(reservation => {
      const arrivalTime = reservation?.reservationDetails?.arrivalTime;

      if (!arrivalTime) {
        return false;
      }

      const parsed = this.parseReservationArrivalTime(arrivalTime);

      if (!parsed) {
        return false;
      }

      return this.reservationService.hourPartBelongsToShift(
        shift,
        parsed.hours,
        parsed.mins,
        parsed.mins
      );
    });
  }

  private parseReservationArrivalTime(value: string): { hours: number; mins: number } | null {
    const match = value.match(/(\d{2}):(\d{2})/);

    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const mins = Number(match[2]);

    if (Number.isNaN(hours) || Number.isNaN(mins)) {
      return null;
    }

    return { hours, mins };
  }


  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
